import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Check, X, Pencil } from "lucide-react";

export default function AdminVotes() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchVotes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/lab/votes");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVotes();
  }, [fetchVotes]);

  const addFlavor = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setBusy(true);
    try {
      await adminApi.post("/admin/lab/flavors", { label: newLabel.trim() });
      setNewLabel("");
      toast.success("Saveur ajoutée");
      fetchVotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'ajout");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (f) => {
    setEditingId(f.flavor_id);
    setEditLabel(f.label);
  };

  const saveEdit = async (id) => {
    if (!editLabel.trim()) return;
    setBusy(true);
    try {
      await adminApi.patch(`/admin/lab/flavors/${id}`, { label: editLabel.trim() });
      toast.success("Saveur mise à jour");
      setEditingId(null);
      fetchVotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const deleteFlavor = async (f) => {
    if (
      !window.confirm(
        `Supprimer "${f.label}" ?\n${f.votes ? `⚠️ Les ${f.votes} vote(s) associé(s) seront aussi effacés.` : ""}`
      )
    )
      return;
    setBusy(true);
    try {
      await adminApi.delete(`/admin/lab/flavors/${f.flavor_id}`);
      toast.success("Saveur supprimée");
      fetchVotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-coral" />
      </div>
    );

  return (
    <div data-testid="admin-votes-page">
      <h1 className="font-display text-4xl">Votes Amyu Lab</h1>
      <p className="text-amyu-muted mt-2">
        {data.total} vote{data.total > 1 ? "s" : ""} au total · gère les saveurs proposées
      </p>

      <form
        onSubmit={addFlavor}
        className="mt-6 flex flex-col sm:flex-row gap-2 bg-white rounded-[24px] p-5"
        data-testid="admin-votes-add-form"
      >
        <input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="Nom de la nouvelle saveur (ex: Tiramisu Café Caramel)"
          data-testid="admin-votes-new-label"
          className="flex-1 bg-amyu-cream rounded-full px-4 py-2.5 outline-none focus:ring-2 focus:ring-coral/30"
        />
        <button
          type="submit"
          disabled={busy || !newLabel.trim()}
          data-testid="admin-votes-add-submit"
          className="btn-coral inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Plus size={16} /> Ajouter
        </button>
      </form>

      <div className="mt-6 bg-white rounded-[28px] p-6 md:p-7">
        {data.flavors.length === 0 ? (
          <div className="text-center text-amyu-muted py-8">
            Aucune saveur pour l&apos;instant.
          </div>
        ) : (
          data.flavors.map((f, i) => (
            <div
              key={f.flavor_id}
              className="mb-5 last:mb-0"
              data-testid={`admin-vote-${f.flavor_id}`}
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {i === 0 && data.total > 0 && (
                    <span className="text-coral shrink-0">★</span>
                  )}
                  {editingId === f.flavor_id ? (
                    <>
                      <input
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        className="flex-1 bg-amyu-cream rounded-full px-3 py-1 text-sm outline-none focus:ring-2 focus:ring-coral/30"
                        data-testid={`admin-vote-edit-input-${f.flavor_id}`}
                      />
                      <button
                        onClick={() => saveEdit(f.flavor_id)}
                        disabled={busy}
                        data-testid={`admin-vote-edit-save-${f.flavor_id}`}
                        className="w-8 h-8 rounded-full bg-coral text-white inline-flex items-center justify-center shrink-0"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="w-8 h-8 rounded-full bg-amyu-cream inline-flex items-center justify-center shrink-0"
                      >
                        <X size={14} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="font-medium truncate">{f.label}</span>
                      <button
                        onClick={() => startEdit(f)}
                        data-testid={`admin-vote-edit-${f.flavor_id}`}
                        title="Modifier"
                        className="w-7 h-7 rounded-full text-amyu-muted hover:text-coral hover:bg-coral/10 inline-flex items-center justify-center shrink-0"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => deleteFlavor(f)}
                        disabled={busy}
                        data-testid={`admin-vote-delete-${f.flavor_id}`}
                        title="Supprimer"
                        className="w-7 h-7 rounded-full text-amyu-muted hover:text-coral hover:bg-coral/10 inline-flex items-center justify-center shrink-0"
                      >
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
                <span className="text-sm shrink-0">
                  <span className="font-medium">{f.votes}</span>
                  <span className="text-amyu-muted ml-2">{f.percent}%</span>
                </span>
              </div>
              <div className="h-3 rounded-full bg-amyu-cream overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    i === 0 && data.total > 0 ? "bg-coral" : "bg-amyu-pistachio"
                  }`}
                  style={{ width: `${f.percent}%` }}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
